import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { authApi } from '@/lib/api/auth';
import type { ConsoleAdmin, LoginInput, TwoFactorVerifyInput, ConsoleAuthContextType } from '@/types';

const AuthContext = createContext<ConsoleAuthContextType | null>(null);

export function ConsoleAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<ConsoleAdmin | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    authApi.getMe()
      .then((admin) => {
        setAdmin(admin);
        Sentry.setUser({ id: admin.id, email: admin.email });
      })
      .catch(() => setAdmin(null))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const result = await authApi.login(input);
    if (!result.requiresTwoFactor) {
      setAdmin(result.admin);
      Sentry.setUser({ id: result.admin.id, email: result.admin.email });
    }
    return {
      requiresTwoFactor: result.requiresTwoFactor,
      tempToken: result.requiresTwoFactor ? result.tokens?.accessToken : undefined,
    };
  }, []);

  const verifyTwoFactor = useCallback(async (input: TwoFactorVerifyInput) => {
    const result = await authApi.verifyTwoFactor(input);
    setAdmin(result.admin);
    Sentry.setUser({ id: result.admin.id, email: result.admin.email });
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    Sentry.setUser(null);
    setAdmin(null);
  }, []);

  const hasPermission = useCallback((permission: string) => {
    if (!admin) return false;
    return admin.role.permissions.includes(permission) || admin.role.permissions.includes('*');
  }, [admin]);

  const refreshAdmin = useCallback(async () => {
    try {
      const freshAdmin = await authApi.getMe();
      setAdmin(freshAdmin);
    } catch {
      // Silently fail - admin may have been logged out
    }
  }, []);

  return (
    <AuthContext.Provider value={{ admin, isAuthenticated: !!admin, isLoading, login, verifyTwoFactor, logout, hasPermission, refreshAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useConsoleAuth(): ConsoleAuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useConsoleAuth must be used within ConsoleAuthProvider');
  return ctx;
}
